import { authService } from "../auth/services/authService";
import { redactUrl } from "../telemetry/scrub";
import { getTelemetrySessionId, trackEvent } from "../telemetry/telemetry";
import { apiBaseUrl } from "./apiConfig";
import { ApiError, type RequestOptions } from "./apiTypes";

export const createApiService = (controllerPath: string) => {
  const request = async <T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> => {
    const { skipAuth = false, headers = {}, ...restOptions } = options;

    // Build headers
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...(headers as Record<string, string>),
    };
    // Lets backend logs be joined with the browser telemetry session.
    const sessionId = getTelemetrySessionId();
    if (sessionId) requestHeaders["X-Client-Id"] = sessionId;

    // Add auth token if not skipped
    if (!skipAuth) {
      const token = await authService.getAccessToken();
      if (token) {
        requestHeaders["Authorization"] = `Bearer ${token}`;
      }
    }
    // Build full URL
    const path = `${controllerPath}/${endpoint}`.replace(/\/\/+/g, "/");
    const url = `${apiBaseUrl}/${path}`;
    console.log("API Request URL:", url);

    const method = (restOptions.method ?? "GET").toUpperCase();
    const telemetryEndpoint = redactUrl(`/${path}`);
    const startedAt = performance.now();
    const elapsed = () => Math.round(performance.now() - startedAt);

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        ...restOptions,
        headers: requestHeaders,
      });
    } catch (error) {
      // Cancellation (e.g. React Query aborting a stale query) is not a failure.
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        trackEvent("api.request_failed", {
          method, endpoint: telemetryEndpoint, failure: "network", durationMs: elapsed(),
        });
      }
      throw error;
    }

    // Handle response
    if (!response.ok) {
      trackEvent("api.request_failed", {
        method,
        endpoint: telemetryEndpoint,
        failure: "http",
        status: response.status,
        correlationId: response.headers.get("X-Correlation-Id") ?? undefined,
        durationMs: elapsed(),
      });
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData: unknown;

      try {
        errorData = await response.json();
        if (
          errorData &&
          typeof errorData === "object" &&
          "error" in errorData &&
          typeof errorData.error === "string"
        ) {
          errorMessage = errorData.error;
        } else if (
          errorData &&
          typeof errorData === "object" &&
          "message" in errorData &&
          typeof errorData.message === "string"
        ) {
          errorMessage = errorData.message;
        }
      } catch {
        // If response is not JSON, use status text
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Parse JSON response
    try {
      return await response.json();
    } catch {
      // If no JSON body, return empty object
      return {} as T;
    }
  };

  return {
    get: async <T>(endpoint: string, options?: RequestOptions): Promise<T> => {
      return request<T>(endpoint, { ...options, method: "GET" });
    },

    post: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    put: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "PUT",
        body: JSON.stringify(data),
      });
    },

    patch: async <T>(
      endpoint: string,
      data?: unknown,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, {
        ...options,
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    delete: async <T>(
      endpoint: string,
      options?: RequestOptions
    ): Promise<T> => {
      return request<T>(endpoint, { ...options, method: "DELETE" });
    },
  };
};

export type ApiServiceType = ReturnType<typeof createApiService>;
